import { Skeleton, Stack, Typography } from '@mui/material'
import { useMemo } from 'react'
import { Icons, ItemImageArea, PointDisplay } from 'utoniq-ui'

import { Item, StoreItemDetailFragment } from '@/clients/utoniq-core/schema'
import { styleSets } from '@/components/styles'
import { useTranslation } from '@/core'
import { Organism } from '@/core/architecture'

type OrderItemProps = {
  item?: StoreItemDetailFragment | Item
  sku?: Partial<Item['sku']>
  quantity: number
  arrow?: boolean
}
export const OrderItem: Organism<OrderItemProps> = ({
  item,
  quantity,
  sku,
  arrow,
}) => {
  const { t } = useTranslation('storeItemPurchaseComplete')
  const unitName = sku?.unitName || item?.sku?.unitName
  const uri = sku?.thumbnail?.uri || item?.sku?.thumbnail?.uri

  const formattedPrice = useMemo(() => {
    if (sku?.price) {
      return sku?.formattedPrice ?? '0'
    }
    return item?.formattedPrice ?? '0'
  }, [item?.formattedPrice, sku?.formattedPrice, sku?.price])

  return (
    <Stack direction="row" spacing={3} className="orderItem">
      {uri || item?.thumbnail?.front?.mediumUri ? (
        <ItemImageArea
          size="small"
          src={(uri || item?.thumbnail.front?.mediumUri) ?? ''}
        />
      ) : (
        <Skeleton variant="rectangular" width={60} height={96} />
      )}
      <Stack flex={1} spacing={1} sx={{ minWidth: 0 }}>
        <Stack sx={{ pt: !unitName ? 2 : 0 }}>
          <Stack direction="row" alignItems="center" gap={4}>
            <Typography variant="subtitle1" sx={styleSets.lineCamp(2)}>
              {item?.title}
            </Typography>
            {arrow && <Icons.RightArrow fontSize="xSmall" sx={{ mb: '3px' }} />}
          </Stack>
          {!!unitName && <Typography variant="caption">{unitName}</Typography>}
        </Stack>
        <Stack direction="row" spacing={2} alignItems="baseline">
          {item?.paymentType === 'cash' ? (
            <Typography variant="subtitle1">
              {t('itemPrice', '¥', { price: formattedPrice })}
            </Typography>
          ) : (
            <PointDisplay size="small" point={formattedPrice} />
          )}
          <Typography variant="subtitle1">
            {t('itemQuantity', '数量', { quantity })}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  )
}
